import {
  createElement,
  forwardRef,
  useEffect,
  useCallback,
  useRef,
  useState,
  ReactNode,
  DOMAttributes,
} from 'react';
import { TiledLayoutProps } from './types';
import { Toolbar, ToolbarItem } from '../Toolbar';
import { ReactFocusEvent } from '../../react-synthetic-events';
import { useIsRTL } from '../../locale';
import { useRefArray } from '../../hooks/useRefArray';
import { createUseTextLocalizer } from '../../locale/createUseTextLocalizer';
import {
  ArrowSmallLeft,
  ArrowSmallRight,
  ArrowSmallLeftEnd,
  ArrowSmallRightEnd,
} from '@sas/react-svg';
import { ButtonVariant } from '../Button';
import { useResizeObserverRef } from '../../hooks/useResizeObserver';
import { setRef } from '../../util/ref-util';
import { postfixTestId } from '../../util/testId';
import { ZeroState } from '../ZeroState';
import { SelectInfo } from '../Tile/types';
import { TilesContextProvider } from '../Tile';
import bundle from './NovaComponents-TiledLayout-gui-icu.properties';
import cn from './TiledLayout.css.json';
import tileCn from '../Tile/Tile.css.json';
import './TiledLayout.css';

const useTextLocalizer = createUseTextLocalizer(bundle);

export const TiledLayout = forwardRef<HTMLDivElement, TiledLayoutProps>(function TiledLayout(
  {
    length,
    renderTile,
    pageSize = 50,
    page = 0,
    width,
    height,
    loadData,
    tilesContainerRef,
    testId,
    disableInteractiveMode = false,
    selectionMode = 'none',
    selectedTiles,
    getTileKey,
    onTileSelect,
    zeroStateText,
    ariaLabel,
  },
  ref
) {
  const localize = useTextLocalizer();
  const hasPaging = pageSize > 0 && pageSize < length;
  const numTiles = hasPaging ? pageSize : length;
  const [numColumns, setNumColumns] = useState(hasPaging ? pageSize : length);
  const [tiles, setTiles] = useState<ReactNode[]>([]);
  const bottomBumperRef = useRef<HTMLDivElement>(null);
  const topBumperRef = useRef<HTMLDivElement>(null);
  const internalTilesContainerRef = useRef<HTMLDivElement>(null);
  const { refs: tileRefs, createSetRef: createTileRef } = useRefArray<HTMLDivElement>();

  const startIndex = useRef(hasPaging ? page * pageSize : 0);
  const endIndex = useRef(Math.min(startIndex.current + numTiles - 1, length - 1));
  const [currentTileIndex, setCurrentTileIndex] = useState(0);
  const ignoreScroll = useRef(false);
  const ignoreFocus = useRef(true);
  const isRTL = useIsRTL();

  const isSelected = useCallback(
    (index?: number) => {
      if (index === undefined) return false;
      // if the selection mode is none or there are no selected tiles
      // return false
      if (selectionMode === 'none' || !selectedTiles) return false;

      // if there is no key for this tile, return false
      const tileKey = getTileKey?.(index);
      if (!tileKey) return false;

      // if this is single selection mode, only select the first tile
      // in the set of selected tiles
      if (selectionMode === 'single') {
        const [firstKey] = selectedTiles;
        return firstKey === tileKey;
      }

      // this is desktop or multiple selection mode
      // so see if this tile is in the set of selected tiles
      return selectedTiles.has(tileKey);
    },
    [selectionMode, selectedTiles, getTileKey]
  );

  const createTile = useCallback(
    (i: number) => {
      const getTileContainerProps = (index: number): DOMAttributes<HTMLDivElement> => ({
        onFocus: () => {
          setCurrentTileIndex(index);
        },
      });

      return (
        <div key={`tile-${i}`} data-index={i} {...getTileContainerProps(i)}>
          {renderTile(i)}
        </div>
      );
    },
    [renderTile]
  );

  useEffect(() => {
    const newTiles: ReactNode[] = [];
    startIndex.current = hasPaging ? page * pageSize : 0;
    endIndex.current = hasPaging ? startIndex.current + numTiles - 1 : length - 1;
    if (hasPaging === false) {
      for (let i = 0; i < length; i++) {
        newTiles.push(createTile(i));
      }
    } else {
      while (startIndex.current % numColumns !== 0 && startIndex.current < length)
        startIndex.current++;
      while ((endIndex.current + 1) % numColumns !== 0 && endIndex.current < length)
        endIndex.current++;
      endIndex.current = Math.min(endIndex.current, length - 1);
      // if the numColumns is 0, the length may have changed
      // and the numColumns needs to be recalculated --
      // only create tiles for one page
      if (numColumns === 0) {
        startIndex.current = 0;
        endIndex.current = Math.min(endIndex.current, pageSize);
      }
      for (let i = startIndex.current; i <= endIndex.current; i++) {
        newTiles.push(createTile(i));
      }
    }
    setTiles(newTiles);
  }, [page, pageSize, length, createTile, hasPaging, numTiles, numColumns]);

  const scrollToTop = useCallback(() => {
    if (tilesContainerRef?.current) tilesContainerRef.current.scrollTop = 0;
    else if (internalTilesContainerRef?.current) internalTilesContainerRef.current.scrollTop = 0;
  }, [tilesContainerRef, internalTilesContainerRef]);

  const totalPages = hasPaging ? Math.ceil(length / pageSize) : 1;
  const toolbarItems: ToolbarItem[] = [
    { type: 'ToolbarSeparator' },
    {
      type: 'Button',
      icon: { icon: ArrowSmallLeftEnd },
      variant: ButtonVariant.Icon,
      disabled: page === 0,
      onPress: () => {
        // do not send focus to the tiles when pressing the paging buttons
        ignoreFocus.current = true;
        loadData?.(0);
        setCurrentTileIndex(0);
      },
      tooltip: localize('firstPage.tooltip.label'),
      testId: postfixTestId(testId, '-button-firstPage'),
    },
    {
      type: 'Button',
      icon: { icon: ArrowSmallLeft },
      variant: ButtonVariant.Icon,
      disabled: page === 0,
      onPress: () => {
        loadData?.(page - 1);
        // do not send focus to the tiles when pressing the paging buttons
        ignoreFocus.current = true;
        let startTile = (page - 1) * pageSize;
        while (startTile % numColumns !== 0 && startTile < length) startTile++;
        setCurrentTileIndex(startTile);
      },
      tooltip: localize('previousPage.tooltip.label'),
      testId: postfixTestId(testId, '-button-previousPage'),
    },
    {
      type: 'Button',
      icon: { icon: ArrowSmallRight },
      variant: ButtonVariant.Icon,
      disabled: page === totalPages - 1,
      onPress: () => {
        loadData?.(page + 1);
        // do not send focus to the tiles when pressing the paging buttons
        ignoreFocus.current = true;
        setCurrentTileIndex(endIndex.current);
      },
      tooltip: localize('nextPage.tooltip.label'),
      testId: postfixTestId(testId, '-button-nextPage'),
    },
    {
      type: 'Button',
      icon: { icon: ArrowSmallRightEnd },
      variant: ButtonVariant.Icon,
      disabled: page === totalPages - 1,
      onPress: () => {
        loadData?.(totalPages - 1);
        // do not send focus to the tiles when pressing the paging buttons
        ignoreFocus.current = true;
        const nextToLastPage = Math.max(totalPages - 2, 0);
        let lastPageFirstTile = nextToLastPage * pageSize + pageSize;
        while (lastPageFirstTile % numColumns !== 0 && lastPageFirstTile < length)
          lastPageFirstTile++;
        setCurrentTileIndex(lastPageFirstTile);
      },
      tooltip: localize('lastPage.tooltip.label'),
      testId: postfixTestId(testId, '-button-lastPage'),
    },
  ];

  const [setResizeRef] = useResizeObserverRef(() => {
    const tileOffset = tileRefs[0]?.offsetTop ?? 0;
    // if the tiles haven't painted yet, just return...we're not ready
    if (tileOffset === 0 || tileRefs.length === 0) return;

    let newTileOffset;
    let tempNumColumns = numColumns;
    for (let i = 1; i < numTiles; i++) {
      newTileOffset = tileRefs[i]?.offsetTop ?? 0;
      tempNumColumns = i;
      if (newTileOffset !== tileOffset) break;
      else if (i === numTiles - 1) tempNumColumns = numTiles;
    }
    setNumColumns(tempNumColumns);
  });

  useEffect(() => {
    if (!ignoreScroll.current) {
      scrollToTop();
    } else {
      ignoreScroll.current = false;
    }
  }, [page, scrollToTop]);

  useEffect(() => {
    if (!ignoreFocus.current) tileRefs[currentTileIndex]?.focus();
  }, [tileRefs, currentTileIndex, page, pageSize, tiles]);

  const keyDownHandler = (event: React.KeyboardEvent): void => {
    // if focus is anywhere other than a Tile in this TiledLayout, do nothing
    if (!document.activeElement?.classList.contains(tileCn.tile)) return;
    ignoreFocus.current = false;

    const columnIndex = currentTileIndex % numColumns;
    const selectTile = (index: number) => {
      onTileSelect?.({
        selectionArea: 'tile',
        index,
        altKey: event.altKey,
        ctlKey: event.ctrlKey,
        metaKey: event.metaKey,
        shiftKey: event.shiftKey,
      });
    };

    switch (event.key) {
      case 'Tab':
        if (!event.shiftKey) {
          bottomBumperRef.current?.focus({ preventScroll: true });
        } else {
          topBumperRef.current?.focus();
        }
        break;
      case isRTL ? 'ArrowRight' : 'ArrowLeft':
        event.preventDefault();
        if (currentTileIndex > 0) {
          if (currentTileIndex <= startIndex.current) {
            if (hasPaging && page > 0) {
              loadData?.(page - 1);
            }
          }
          setCurrentTileIndex(currentTileIndex - 1);
          if (event.shiftKey) selectTile(currentTileIndex - 1);
        }
        break;
      case isRTL ? 'ArrowLeft' : 'ArrowRight':
        event.preventDefault();
        if (currentTileIndex < length - 1) {
          if (currentTileIndex >= endIndex.current) {
            if (hasPaging && page + 1 < totalPages) {
              loadData?.(page + 1);
            }
          }
          setCurrentTileIndex(currentTileIndex + 1);
          if (event.shiftKey) selectTile(currentTileIndex + 1);
        }
        break;
      case 'ArrowDown':
        if (currentTileIndex + numColumns < length) {
          // if the tile on the next row is in this page, go to it
          if (currentTileIndex + numColumns <= endIndex.current) {
            setCurrentTileIndex(currentTileIndex + numColumns);
          } else {
            // else load another page and then go to the appropriate tile
            if (hasPaging && page + 1 < totalPages) {
              // check to see if we've already loaded the next page to fill empty spaces
              loadData?.(page + 1);
              setCurrentTileIndex(currentTileIndex + numColumns);
            }
          }
          if (event.shiftKey) {
            selectTile(currentTileIndex + numColumns);
          }
        }
        event.preventDefault();
        break;
      case 'ArrowUp':
        if (currentTileIndex - numColumns >= 0) {
          // if the tile on the previous row is in this page, go to it
          if (currentTileIndex - numColumns >= startIndex.current) {
            setCurrentTileIndex(currentTileIndex - numColumns);
          } else {
            // else load another page and then go to the appropriate tile
            if (hasPaging && page > 0) {
              loadData?.(page - 1);
              setCurrentTileIndex(currentTileIndex - numColumns);
              ignoreScroll.current = true;
            }
          }
          if (event.shiftKey) {
            selectTile(currentTileIndex - numColumns);
          }
        }
        event.preventDefault();
        break;
      case 'Home':
        if (event.ctrlKey) {
          if (hasPaging && page > 0 && currentTileIndex === startIndex.current) {
            loadData?.(page - 1);
            let startTile = (page - 1) * pageSize;
            while (startTile % numColumns !== 0 && startTile < length) startTile++;
            setCurrentTileIndex(startTile);
          } else {
            setCurrentTileIndex(startIndex.current);
          }
        } else {
          setCurrentTileIndex(numColumns * Math.floor(currentTileIndex / numColumns));
        }
        event.preventDefault();
        break;
      case 'End':
        if (event.ctrlKey) {
          if (hasPaging && page + 1 < totalPages && currentTileIndex === endIndex.current) {
            loadData?.(page + 1);
            let endTile = (page + 1) * pageSize + pageSize - 1;
            while ((endTile + 1) % numColumns !== 0 && endTile < length) endTile++;
            setCurrentTileIndex(endTile);
          } else {
            setCurrentTileIndex(endIndex.current);
          }
        } else {
          setCurrentTileIndex(
            Math.min(currentTileIndex + (numColumns - columnIndex - 1), endIndex.current)
          );
        }
        event.preventDefault();
        break;
      case 'PageUp':
        if (hasPaging && page > 0) {
          loadData?.(page - 1);
          let startTile = (page - 1) * pageSize;
          while (startTile % numColumns !== 0 && startTile < length) startTile++;
          setCurrentTileIndex(startTile);
        }
        event.preventDefault();
        break;
      case 'PageDown':
        if (hasPaging && page + 1 < totalPages) {
          loadData?.(page + 1);
          setCurrentTileIndex(endIndex.current);
        }
        event.preventDefault();
        break;
    }
  };

  const bottomBumperFocusHandler = (event: ReactFocusEvent): void => {
    const focusFromBelow = event.relatedTarget !== tileRefs[currentTileIndex];
    if (focusFromBelow) {
      tileRefs[currentTileIndex]?.focus();
    } else {
      // when leaving the TiledLayout, set ignoreFocus back to true
      // so TiledLayout won't grab focus if the tiles change
      ignoreFocus.current = true;
    }
  };

  const topBumperFocusHandler = (event: ReactFocusEvent): void => {
    const focusFromAbove = event.relatedTarget !== tileRefs[currentTileIndex];
    if (focusFromAbove) {
      tileRefs[currentTileIndex]?.focus();
    } else {
      // when leaving the TiledLayout, set ignoreFocus back to true
      // so TiledLayout won't grab focus if the tiles change
      ignoreFocus.current = true;
    }
  };

  const topBumperId = postfixTestId(testId, 'topBumper');
  const bottomBumperId = postfixTestId(testId, 'bottomBumper');

  const hideCheckbox = selectionMode !== 'multiple';
  const onSelect = (info: SelectInfo) => {
    onTileSelect?.(info);
  };
  return (
    <TilesContextProvider
      value={{ hideCheckbox, disableInteractiveMode, onSelect, isSelected, createTileRef }}
    >
      <div
        data-testid={testId}
        ref={(element) => {
          setRef(ref, element);
          setResizeRef(element);
        }}
        className={cn.tiledLayout}
        style={{
          width,
          height,
        }}
        aria-label={ariaLabel ?? localize('tiledItems.aria')}
      >
        {length > 0 ? (
          <div className={cn.content}>
            <div
              ref={topBumperRef}
              tabIndex={0}
              onFocus={topBumperFocusHandler}
              data-testid={topBumperId}
            />
            <div
              ref={tilesContainerRef ?? internalTilesContainerRef}
              className={cn.tilesContainer}
              onKeyDown={keyDownHandler}
            >
              {tiles}
            </div>
            <div
              ref={bottomBumperRef}
              tabIndex={0}
              onFocus={bottomBumperFocusHandler}
              data-testid={bottomBumperId}
            />
          </div>
        ) : (
          <ZeroState
            className={cn.content}
            text={zeroStateText ?? localize('noItems.message')}
            style={{
              height: '100%',
            }}
          />
        )}
        {hasPaging && length > 0 ? <Toolbar items={toolbarItems} className={cn.toolbar} /> : null}
      </div>
    </TilesContextProvider>
  );
});
